import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// THE CLUB'S JOIN-REQUEST QUEUE — the browser half (#710).
//
// Three calls, all behind `member.manage` and the resolved tenant, so there is
// NO CLUB TO NAME here: `apiFetch` carries the organization header, and the
// server filters by the scope it resolves from it. A club id in any of these
// signatures would be a club naming whose queue it wants to read.
//
// ── A DECISION SAYS WHAT IT MEANS FOR THE FUTURE ──────────────────
//
// Rejecting REQUIRES a reapplication policy — `allowed`, `after` with a date, or
// `blocked` — and the API refuses the call without one: the request always
// says what the club chose. WHAT THE DIALOG OFFERS FIRST is the owner's
// decision of 2026-11-02 (#729): a 30-day cooling-off, which the club may
// change before it confirms. The default lives in the browser, where a person
// sees it; the API still records only what was sent.
//
// AND A REJECTION CAN BE UNDONE (#729): approved directly, or reopened back
// into the queue. Both are decisions, behind the same right as the others.
// ─────────────────────────────────────────────────────────────────────

/**
 * The cooling-off the reject dialog offers first, in days (owner decision
 * 2026-11-02, #729): «after a rejection the player may ask again after 30
 * days». The club can pick another date, or another answer, before confirming.
 */
export const JOIN_REQUEST_COOLING_OFF_DAYS = 30;

export type ReapplicationPolicy = 'allowed' | 'after' | 'blocked';

export interface JoinRequest {
  id: string;
  status: string;
  requestedAt: string;
  decidedAt: string | null;
  reapplication: ReapplicationPolicy | null;
  reapplyAfter: string | null;
  /**
   * How many times this person has asked this club, this ask included (#729).
   * Above one, the queue says «asked before». 1 when the server sends none.
   */
  askCount: number;
  /**
   * The PLAYER, which is what a club recognises, and how to reach them
   * (owner decision, 2026-10-20). Never the account id. The id is the legacy
   * one — the id the portal's player list and the player card match on.
   */
  player: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    /**
     * The rating a club may see of any player (#1067), for the applicant
     * card: an applicant is usually not in the club's own player list, so the
     * ordinary card cannot show them. Null until they have one. The level is
     * derived from it in the browser.
     */
    elo: number | null;
  };
}

export type JoinRequestListOutcome =
  | { status: 'ok'; requests: JoinRequest[] }
  /** Signed out, or no `member.manage` here. The caller decides what to say. */
  | { status: 'refused' }
  /**
   * The server answered, and a row was not the contract.
   *
   * THE WHOLE RESPONSE FAILS (#735 review). The first version dropped the bad
   * rows and returned the rest — which hides outstanding applications from the
   * people whose job is to answer them, and if every row is malformed the panel
   * disappears ENTIRELY, looking exactly like «nobody has asked».
   *
   * Refusing to render a half-empty row was right; making the record invisible
   * was not. A club seeing «something is wrong» can ask somebody; a club seeing
   * an empty queue cannot, because nothing tells them to.
   */
  | { status: 'protocol' }
  | { status: 'unreachable' };

const POLICIES: readonly string[] = ['allowed', 'after', 'blocked'];

/** One row, or null when the shape is not the contract. */
function readRequest(raw: unknown): JoinRequest | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const player = (r.player ?? {}) as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id === '') return null;
  if (typeof r.status !== 'string' || r.status === '') return null;
  if (typeof r.requestedAt !== 'string' || !Number.isFinite(Date.parse(r.requestedAt))) return null;
  if (typeof player.id !== 'string' || typeof player.name !== 'string') return null;
  return {
    id: r.id,
    status: r.status,
    requestedAt: r.requestedAt,
    decidedAt: typeof r.decidedAt === 'string' ? r.decidedAt : null,
    reapplication:
      typeof r.reapplication === 'string' && POLICIES.includes(r.reapplication)
        ? (r.reapplication as ReapplicationPolicy)
        : null,
    reapplyAfter: typeof r.reapplyAfter === 'string' ? r.reapplyAfter : null,
    // OPTIONAL like the contact fields: a missing count is a first ask, not a
    // broken row — the note is a hint, and its absence costs nothing.
    askCount:
      typeof r.askCount === 'number' && Number.isInteger(r.askCount) && r.askCount > 0
        ? r.askCount
        : 1,
    player: {
      id: player.id,
      name: player.name,
      // OPTIONAL, not part of what makes a row the contract: a row with no
      // address is an applicant with none, not a broken queue.
      email: typeof player.email === 'string' && player.email !== '' ? player.email : null,
      phone: typeof player.phone === 'string' && player.phone !== '' ? player.phone : null,
      elo: typeof player.elo === 'number' && Number.isFinite(player.elo) ? player.elo : null,
    },
  };
}

export async function listJoinRequests(
  status: 'pending' | 'rejected' | 'all' = 'pending',
): Promise<JoinRequestListOutcome> {
  let res: Response;
  try {
    res = await apiFetch(`/members/join-requests?status=${status}`);
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 401 || res.status === 403) return { status: 'refused' };
  if (!res.ok) return { status: 'unreachable' };

  const body = (await res.json().catch(() => null)) as { requests?: unknown } | null;
  if (!Array.isArray(body?.requests)) return { status: 'protocol' };

  // ONE BAD ROW FAILS THE WHOLE LIST. A queue is a list of people a club is
  // about to decide about: a row rendered half-empty is one somebody might
  // approve without knowing who, and a row silently DROPPED is an application
  // that never reaches anybody. The second is worse, and it was what the first
  // version did (#735 review).
  const parsed = body.requests.map(readRequest);
  if (parsed.some((r) => r === null)) return { status: 'protocol' };
  return { status: 'ok', requests: parsed as JoinRequest[] };
}

export type DecisionOutcome =
  | { status: 'decided' }
  /** Somebody else answered it first — the queue is stale, not the caller wrong. */
  | { status: 'already_decided' }
  | { status: 'not_found' }
  | { status: 'refused' }
  /**
   * The server refused the DECISION ITSELF — a policy it will not record.
   *
   * ITS OWN OUTCOME (#735 review). It used to fall into `unreachable`, so a
   * cooling-off date the API rejects as already past was reported as «could not
   * reach the server» — sending somebody to check their connection over a
   * problem in the form in front of them. The reason is a CODE, never a
   * sentence: the server does not know which language this browser is in.
   */
  | { status: 'invalid'; reason: string }
  /** Reopening a rejection of somebody who has become a member since (#729). */
  | { status: 'already_member' }
  | { status: 'unreachable' };

export async function approveJoinRequest(id: string): Promise<DecisionOutcome> {
  return decide(`/members/join-requests/${encodeURIComponent(id)}/approve`, undefined);
}

export async function rejectJoinRequest(
  id: string,
  policy: { reapplication: ReapplicationPolicy; reapplyAfter?: string },
): Promise<DecisionOutcome> {
  return decide(`/members/join-requests/${encodeURIComponent(id)}/reject`, policy);
}

/**
 * Undo a rejection by putting the request back in the queue (#729). The person's
 * own ask stands — its date and its count — and the club decides it afresh.
 * Approving a rejected request directly is `approveJoinRequest`.
 */
export async function reopenJoinRequest(id: string): Promise<DecisionOutcome> {
  return decide(`/members/join-requests/${encodeURIComponent(id)}/reopen`, undefined);
}

async function decide(path: string, body: unknown): Promise<DecisionOutcome> {
  let res: Response;
  try {
    res = await apiFetch(path, {
      method: 'POST',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.ok) return { status: 'decided' };
  if (res.status === 404) return { status: 'not_found' };
  if (res.status === 401 || res.status === 403) return { status: 'refused' };
  if (res.status === 400) {
    const parsed = (await res.json().catch(() => null)) as { error?: string } | null;
    return { status: 'invalid', reason: typeof parsed?.error === 'string' ? parsed.error : '' };
  }
  if (res.status === 409) {
    const parsed = (await res.json().catch(() => null)) as { error?: string } | null;
    // `not_rejected` is the reopen's way of saying the same thing: somebody
    // changed the request since this list was read.
    if (parsed?.error === 'already_decided' || parsed?.error === 'not_rejected') {
      return { status: 'already_decided' };
    }
    if (parsed?.error === 'already_member') return { status: 'already_member' };
    return { status: 'unreachable' };
  }
  return { status: 'unreachable' };
}
